import type { SortDirection, SortField } from "feeds/SortToggle.types";
import { useCallback } from "react";
import type { DateDisplayMode } from "ui/DateDisplay";

import { useSearchParam } from "./useSearchParam";

type UseSortParamOptions = {
  /** Имя search-параметра, хранящего порядок. По умолчанию "sort". */
  paramKey?: string;
  /** Поле сортировки по умолчанию. */
  defaultField?: SortField;
  /** Направление по умолчанию. */
  defaultDirection?: SortDirection;
};

type UseSortParamResult = {
  sortField: SortField;
  sortDirection: SortDirection;
  /** Поле модели для бэкенда/запроса, напр. "-dateModified". */
  ordering: string;
  /** Режим даты на карточке, соответствующий полю сортировки. */
  dateDisplayMode: DateDisplayMode;
  /** Обработчик для `SortToggle.onSortChange` (третий аргумент groupByCase игнорируется). */
  onSortChange: (field: SortField, direction: SortDirection) => void;
};

/**
 * Хранит порядок сортировки в одном search-параметре URL (напр. "-dateCreated") и
 * отдаёт из него `sortField` / `sortDirection` / `ordering` плюс обработчик смены.
 *
 * Иначе два в одном обработчике перетирают друг друга (каждый строит навигацию от своего снимка URL). При смене
 * сортировки сбрасывается `page`, чтобы оставаться совместимым с пагинацией.
 */
export const useSortParam = ({
  paramKey = "sort",
  defaultField = "dateModified",
  defaultDirection = "desc",
}: UseSortParamOptions = {}): UseSortParamResult => {
  const [rawSort, setSort] = useSearchParam(paramKey);

  const defaultOrdering = `${defaultDirection === "desc" ? "-" : ""}${defaultField}`;

  const isValidOrdering =
    rawSort === "dateCreated" ||
    rawSort === "-dateCreated" ||
    rawSort === "dateModified" ||
    rawSort === "-dateModified";
  const ordering = isValidOrdering ? rawSort : defaultOrdering;
  const sortDirection: SortDirection = ordering.startsWith("-")
    ? "desc"
    : "asc";
  const sortField = ordering.replace("-", "") as SortField;
  const dateDisplayMode: DateDisplayMode =
    sortField === "dateCreated" ? "created" : "modified";

  const onSortChange = useCallback(
    (field: SortField, direction: SortDirection) => {
      setSort(`${direction === "desc" ? "-" : ""}${field}`, ["page"]);
    },
    [setSort],
  );

  return { sortField, sortDirection, ordering, dateDisplayMode, onSortChange };
};
